import { Injectable } from '@angular/core';

import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { Decimal } from 'decimal.js';
import { Observable, map } from 'rxjs';

import { IBetslipRootState } from '../../../base/store/state';
import { BetslipGeneratorContextType } from '../../bet-generation/models';
import { IBetslipSummary } from '../../summary/models';
import { getPossibleWinningsNet } from '../../summary/services/betslip-summary-builder.service';
import { BetslipSummaryService } from '../../summary/services/betslip-summary.service';
import BetslipTaxationService from '../../taxation/services/betslip-taxation.service';
import { selectIsValidAccaBoostCombo } from '../selectors';

export interface AccaBoostSummary {
    stake: Decimal | null;
    boostedWinnings: Decimal;
    boostedWinningsNet: Decimal | null;
    originalWinnings: Decimal;
    originalWinningsNet: Decimal | null;
    currentBoost: number;
    boostFactor: number;
    maxRewardAchieved: boolean;
}

@Injectable()
export class AccaBoostSummaryService {
    constructor(
        private summaryService: BetslipSummaryService,
        private taxationService: BetslipTaxationService,
        private store: Store<IBetslipRootState>,
    ) {}

    getSummary$(): Observable<AccaBoostSummary | null> {
        return this._getSummary$();
    }

    private _getSummary$(): Observable<AccaBoostSummary | null> {
        return this.summaryService.getSummary$(BetslipGeneratorContextType.AccaBoost).pipe(
            concatLatestFrom(() => this.store.select(selectIsValidAccaBoostCombo)),
            map(([[, summary], isValidAccaBoostCombo]) => (isValidAccaBoostCombo ? this.mapToAccaBoostSummary(summary) : null)),
        );
    }

    private mapToAccaBoostSummary(summary: IBetslipSummary): AccaBoostSummary | null {
        const accaBoostModifications = summary.slipSummaries.length === 1 && summary.slipSummaries[0].rewardTokenModifications?.accaBoost;

        if (!summary.possibleWinnings || !accaBoostModifications) {
            return null;
        }

        return {
            stake: summary.totalStake,
            boostedWinnings: summary.possibleWinnings,
            boostedWinningsNet: summary.possibleWinningsNet || null,
            originalWinnings: accaBoostModifications.originalWinnings,
            originalWinningsNet:
                getPossibleWinningsNet(
                    accaBoostModifications.originalWinnings,
                    this.taxationService.getTaxationForAmount(accaBoostModifications.originalWinnings),
                ) || null,
            currentBoost: accaBoostModifications.currentBoost.toNumber(),
            boostFactor: accaBoostModifications.boostRatio.toNumber(),
            maxRewardAchieved: accaBoostModifications.currentBoost.equals(accaBoostModifications.maxBoost),
        };
    }
}
